import type { AuthTokens, JwtPayload } from "@/types";

const TOKEN_KEY = "track_tokens";
const TOKENS_CHANGED_EVENT = "auth:tokens-changed";

function notifyTokenChange(): void {
  if (typeof window === "undefined") {
    return;
  }

  window.dispatchEvent(new Event(TOKENS_CHANGED_EVENT));
}

export function onAuthTokensChanged(callback: () => void): () => void {
  if (typeof window === "undefined") {
    return () => {};
  }

  const onStorage = (event: StorageEvent) => {
    if (event.key === TOKEN_KEY) {
      callback();
    }
  };

  window.addEventListener(TOKENS_CHANGED_EVENT, callback);
  window.addEventListener("storage", onStorage);

  return () => {
    window.removeEventListener(TOKENS_CHANGED_EVENT, callback);
    window.removeEventListener("storage", onStorage);
  };
}

export const tokenStorage = {
  get: (): AuthTokens | null => {
    try {
      const tokens = localStorage.getItem(TOKEN_KEY);
      return tokens ? JSON.parse(tokens) : null;
    } catch {
      return null;
    }
  },

  set: (tokens: AuthTokens): void => {
    localStorage.setItem(TOKEN_KEY, JSON.stringify(tokens));
    notifyTokenChange();
  },

  remove: (): void => {
    localStorage.removeItem(TOKEN_KEY);
    notifyTokenChange();
  },

  getAccessToken: (): string | null => {
    const tokens = tokenStorage.get();
    return tokens?.access || null;
  },

  getRefreshToken: (): string | null => {
    const tokens = tokenStorage.get();
    return tokens?.refresh || null;
  },
};

function isJwtPayload(value: unknown): value is JwtPayload {
  return (
    typeof value === "object" &&
    value !== null &&
    "exp" in value &&
    typeof (value as Record<string, unknown>).exp === "number"
  );
}

function decodeJwtPayload(token: string): JwtPayload {
  const parts = token.split(".");
  if (parts.length < 2) throw new Error("Invalid JWT");
  let base64Url = parts[1];
  base64Url = base64Url.replace(/-/g, "+").replace(/_/g, "/");
  const pad = base64Url.length % 4;
  if (pad) {
    base64Url += "=".repeat(4 - pad);
  }
  const json = atob(base64Url);
  const parsed: unknown = JSON.parse(json);
  if (!isJwtPayload(parsed)) {
    throw new Error("Invalid JWT payload");
  }
  return parsed;
}

export const isTokenExpired = (token: string): boolean => {
  try {
    const payload = decodeJwtPayload(token);
    const currentTime = Date.now() / 1000;
    return payload.exp <= currentTime;
  } catch {
    // If we cannot parse, assume expired
    return true;
  }
};

export const shouldRefreshToken = (accessToken: string): boolean => {
  try {
    const payload = decodeJwtPayload(accessToken);
    const currentTime = Date.now() / 1000;
    // Refresh if token expires within 5 minutes
    return payload.exp - currentTime < 300;
  } catch {
    return true;
  }
};
